/**
 * Wosool, Fosool «فصول» and Osool «أصول» — which side of the dashboard a page
 * is on.
 *
 * The manager dashboard is three products over one set of routes: Wosool (the
 * buses and the roster, as it always was), Fosool, the school's teaching side,
 * and Osool, its money. A route says where it belongs with `product`:
 *
 * - `"fosool"` — Fosool's own: its home «الرئيسية» and the Education pages.
 * - `"osool"` — Osool's own: Financial Management, Bus Fees and Billing.
 * - `"shared"` — every sidebar may carry it, and it keeps whichever side you
 *   are on: the people and classes pages, the user menu's pages.
 * - untagged — Wosool's own, which is every route that predates the other
 *   two — Home included, since Fosool has a home of its own.
 *
 * Wosool's sidebar leaves the other sides' own pages out; the product rail on
 * the sidebar's outer edge is the way in.
 *
 * **The URL decides.** A Fosool page opens Fosool and a Wosool page opens
 * Wosool whatever was chosen last, so a bookmark, a refresh and the back
 * button always agree with the sidebar. The remembered side only speaks for
 * shared pages — and only for the person who left it there, because school
 * offices share machines.
 *
 * DOM-free; tested by `scripts/tests/productSwitch.test.mjs`.
 */
import { ROLE_ROOT_ADMIN, canAccessRoute } from "./routeAccess.js";

export const WOSOOL = "wosool";
export const FOSOOL = "fosool";
export const OSOOL = "osool";
export const SHARED = "shared";

/** The sides, in the rail's order. */
export const SIDES = [WOSOOL, FOSOOL, OSOOL];

const LAYOUT = "/manager";

/** A route's side. A route that does not say is Wosool's. */
export function productOfRoute(route) {
  if (route?.product === FOSOOL) return FOSOOL;
  if (route?.product === OSOOL) return OSOOL;
  if (route?.product === SHARED) return SHARED;
  return WOSOOL;
}

/** Every page in a route list, with collapse groups opened up. */
export function flattenRoutes(routes) {
  return (routes || []).flatMap((route) => {
    if (!route) return [];
    return route.collapse ? flattenRoutes(route.views) : [route];
  });
}

const segmentsOf = (path) => path.split("/").filter(Boolean);

const paramCount = (route) =>
  segmentsOf(route.path).filter((part) => part.startsWith(":")).length;

/**
 * The page a pathname opens, or `null` for one no route claims (the 404
 * page). A literal segment outranks a parameter, as React Router ranks them:
 * `/users/parents` is the parents list, not a person called "parents".
 */
export function routeForPath(pathname, routes) {
  if (typeof pathname !== "string" || !pathname.startsWith(`${LAYOUT}/`)) {
    return null;
  }
  const wanted = segmentsOf(pathname.slice(LAYOUT.length));
  const matches = flattenRoutes(routes).filter((route) => {
    if (route.layout !== LAYOUT || typeof route.path !== "string") return false;
    const pattern = segmentsOf(route.path);
    return (
      pattern.length === wanted.length &&
      pattern.every((part, i) => part.startsWith(":") || part === wanted[i])
    );
  });
  if (matches.length === 0) return null;
  return matches.reduce((best, route) =>
    paramCount(route) < paramCount(best) ? route : best
  );
}

/**
 * The sides this person has, in the rail's order: a side is theirs when they
 * can open one of its own pages. Two or more bring out the rail.
 *
 * Shared pages do not count: a side made only of pages another side already
 * shows would be the same menu in another colour. Asked through
 * `canAccessRoute`, like everything else that decides what a menu offers.
 */
export function sidesFor(user, routes) {
  const pages = flattenRoutes(routes);
  return SIDES.filter((side) =>
    pages.some(
      (route) => productOfRoute(route) === side && canAccessRoute(user, route)
    )
  );
}

/**
 * The side to show. A side's own page decides it; a shared page, or a path no
 * route claims, keeps the remembered side. A side this person does not have
 * is never shown — whatever a shared machine remembers — and the first one
 * they do have stands in for it.
 */
export function resolveProduct({ pathname, routes, remembered, sides }) {
  const available = sides && sides.length > 0 ? sides : [WOSOOL];
  const fallback = available[0];
  const route = routeForPath(pathname, routes);
  const side = route ? productOfRoute(route) : SHARED;
  if (side === SHARED) {
    return available.includes(remembered) ? remembered : fallback;
  }
  return available.includes(side) ? side : fallback;
}

/**
 * Wosool's sidebar: every route but the other sides' own. A collapse group
 * keeps its other views; one left with none is hidden by
 * `isRouteVisibleInSidebar`, as any empty group is.
 */
export function wosoolSidebarRoutes(routes) {
  return (routes || []).flatMap((route) => {
    if (!route) return [];
    if (route.collapse) {
      return [{ ...route, views: wosoolSidebarRoutes(route.views) }];
    }
    const side = productOfRoute(route);
    return side === FOSOOL || side === OSOOL ? [] : [route];
  });
}

/**
 * Fosool's or Osool's sidebar for this person.
 *
 * `nav` names pages by path and adds only what the side says differently: its
 * sections and its words. Whether an item shows is always its route's own
 * `allowedRoles`, asked through `canAccessRoute` — the one predicate the
 * route guard asks too — so this menu can never offer a page the guard
 * refuses.
 *
 * An item whose route is missing (its module is off) or closed to this
 * person is dropped, and a section left empty goes with it. Only the side's
 * own pages and shared ones are listed: another side's page would switch the
 * dashboard under the person who clicked it.
 *
 * Entries are `{ path, … }` or `{ items: [{ path, … }], … }`; each item kept
 * comes back with its `route`.
 */
export function sideNav(nav, routes, user, side) {
  const pages = flattenRoutes(routes);
  const resolve = (item) => {
    const route = pages.find(
      (page) => page.layout === LAYOUT && page.path === item.path
    );
    if (!route) return null;
    const routeSide = productOfRoute(route);
    if (routeSide !== side && routeSide !== SHARED) return null;
    if (!canAccessRoute(user, route)) return null;
    return { ...item, route };
  };
  return (nav || []).flatMap((entry) => {
    if (Array.isArray(entry.items)) {
      const items = entry.items.map(resolve).filter(Boolean);
      return items.length > 0 ? [{ ...entry, items }] : [];
    }
    const item = resolve(entry);
    return item ? [item] : [];
  });
}

/** Where a side's menu item links: its page, and the query it opens with. */
export function navHref(item) {
  return `${LAYOUT}${item.path}${item.search || ""}`;
}

/** Fosool's own «الرئيسية», and Wosool's Home. */
export const FOSOOL_HOME = `${LAYOUT}/education`;
export const WOSOOL_HOME = `${LAYOUT}/dashboard`;

/**
 * Which modules a school user's school has on, from the login payload — and
 * every one for root, who has no school of their own and picks one inside
 * each page. `useManagerRoutes` builds the routes from this, and sign-in
 * (`homePath.js`) lands people with it, so the two cannot disagree.
 */
export function modulesFor({ role, settings }) {
  const on = (key) => role === ROLE_ROOT_ADMIN || settings?.[key] === true;
  return {
    education: on("education_enabled"),
    finance: on("finance_enabled"),
    busFees: on("bus_fees_enabled"),
    billing: on("billing_enabled"),
  };
}

/**
 * Osool's home: its first page in `OSOOL_NAV`'s order — the finance
 * overview, else the bus fee list, else billing's overview — or `null` with
 * no money module on.
 */
export function osoolHomeFor(modules) {
  if (modules?.finance) return `${LAYOUT}/finance`;
  if (modules?.busFees) return `${LAYOUT}/bus-fees`;
  if (modules?.billing) return `${LAYOUT}/billing`;
  return null;
}

/**
 * Where someone starts after signing in: the home of the side they were last
 * on (`rememberedFor`), from `homes` — each side's home for this person, or
 * none when the side is not theirs, so a machine that remembers a side never
 * sends anyone else there. Wosool's Home otherwise.
 */
export function homeOfSide({ side, homes }) {
  return (side !== WOSOOL && homes?.[side]) || WOSOOL_HOME;
}

/**
 * Where switching to `side` lands.
 *
 * The page last open on that side, while it is still a page of that side
 * (or a shared one) this person may open. Otherwise the side's first page of
 * its own in `nav` (Fosool's home, Osool's overview), or Wosool's Home.
 */
export function landingFor({ side, lastPath, routes, user, nav }) {
  if (typeof lastPath === "string" && lastPath) {
    const route = routeForPath(lastPath.split("?")[0], routes);
    const routeSide = route ? productOfRoute(route) : null;
    if (
      route &&
      canAccessRoute(user, route) &&
      (routeSide === side || routeSide === SHARED)
    ) {
      return lastPath;
    }
  }
  if (side !== WOSOOL) {
    const items = sideNav(nav, routes, user, side).flatMap(
      (entry) => entry.items || [entry]
    );
    const first =
      items.find((item) => productOfRoute(item.route) === side) || items[0];
    if (first) return navHref(first);
  }
  return WOSOOL_HOME;
}

/**
 * The remembered side and last pages — for the person who left them. Anyone
 * else on the same machine starts on Wosool, with nothing remembered.
 */
export function rememberedFor(stored, userId) {
  if (!userId || !stored || stored.userId !== userId) {
    return { side: WOSOOL, lastPath: {} };
  }
  return {
    side: SIDES.includes(stored.side) ? stored.side : WOSOOL,
    lastPath: { ...(stored.lastPath || {}) },
  };
}
